import MixpanelBrowser, {
  Config,
  RequestOptions,
  Callback,
} from 'mixpanel-browser';

import { Async, SMap } from '../../helpers/types';
import { EventWithPayload, MixPanelEvent } from './events';
import auth, { isProduction } from '@client/components/auth';
import { getVersion } from 'releases';
import { GetAllProgramsQuery, useGetAllProgramsQuery } from 'generated/graphql';
import { useAsyncQuery } from '@client/new/helpers/gql';
import { useUser } from '@client/new/hooks/use-user';

export type MixpanelOptions = {
  name: string;
  options: Partial<Config>;
  token: string;
};

type EventProps = {
  eventName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: SMap<any>;
  optionsOrCb?: RequestOptions | Callback;
  cb?: Callback;
  uid: string;
};

let _isInitialized = false;

export const initializeMixpanel = (config: {
  token: string;
  options: Partial<Config>;
}) => {
  if (_isInitialized) return;
  const debug = import.meta.env.VITE_ENV !== 'production';
  MixpanelBrowser.init(config.token, {
    debug,
    ...config.options,
  });
  MixpanelBrowser.register({
    platform: 'web',
    'WebPage Version': getVersion(),
  });
  _isInitialized = true;
  console.info('Mixpanel initialized', debug);
};

export const track = <T extends MixPanelEvent>(
  { type, payload }: T & { payload: { uid: string | number } },
  props?: Pick<EventProps, 'optionsOrCb' | 'cb'>,
) => {
  if (!_isInitialized) {
    // TODO: we may move those to .env, but that not a priority since it is not a secret
    const devToken = 'c4d78278547dfda40ab55e96d08f206f';
    const prodToken = 'd0cc7240d1640ac4b0cc271807bced6b';
    initializeMixpanel({
      token: isProduction() ? prodToken : devToken,
      options: { debug: !isProduction(), ignore_dnt: true },
    });
  }
  const { uid, ...p } = payload;

  if (type === 'Log In') MixpanelBrowser.people.set(p);
  if (uid) MixpanelBrowser.identify(`${uid}`);

  MixpanelBrowser.track(type, payload, props?.optionsOrCb, props?.cb);
};

type EventPayload<TName extends string, T> = T extends EventWithPayload<
  TName,
  infer P
>
  ? P
  : never;

type EventPayloadsMap = {
  [K in MixPanelEvent['type']]: EventPayload<K, MixPanelEvent>;
};

export type EventName = keyof EventPayloadsMap;

export const trackLogin = (
  payload: { uid: string } & Partial<EventPayloadsMap['Log In']>,
) => {
  const t: EventName = 'Log In';
  track({
    type: t,
    payload: payload as any,
  });
};

export const setUID = (uid: string) => {
  MixpanelBrowser.identify(uid);
};

export const mixTrack = <T extends EventName>(
  {
    type,
    payload,
  }: {
    type: T;
    payload?: Omit<EventPayloadsMap[T], 'Influencer ID' | 'Influencer Name'>;
  },
  props?: Pick<EventProps, 'optionsOrCb' | 'cb'>,
) => {
  if (!isProduction()) {
    console.log('[Mixpanel track]:', type, payload);
  }
  if (!auth.isInfluencer()) return;

  const user = auth.getUser();
  const modifiedPayload = {
    ...payload,
    uid: user.id,
    'Influencer ID': user?.influencer?.id,
    'Influencer Name':
      user.influencer?.firstName + ' ' + user.influencer?.lastName,
  };

  track({ type, payload: modifiedPayload } as any, props);
};

type Product = {
  programId: number;
  collectionType: 'category' | 'program';
  name: string;
  productType: 'otp' | 'subscription';
};
export type PublishedProducts = {
  publishedProductType: Product['productType'] | 'both' | 'none';
  publishedCollectionType: Product['collectionType'] | 'both' | 'none';
  products: Product[];
};

const toPublishedPrograms = (
  v: GetAllProgramsQuery['creator'],
): PublishedProducts => {
  if (!v)
    return {
      products: [],
      publishedProductType: 'none',
      publishedCollectionType: 'none',
    };
  const products = (v.programs = v.programs || [])
    .filter((p) => p.publishedAt && !p.archivedAt)
    .map(
      (p): Product => ({
        programId: p.id,
        collectionType: p.type === 3 ? 'category' : 'program',
        name: p.name || '',
        productType: p.accessType === 'exclusive' ? 'otp' : 'subscription',
      }),
    );

  const otps = products.filter((p) => p.productType === 'otp');
  const subs = products.filter((p) => p.productType === 'subscription');
  const publishedProductType =
    otps.length > 0 && subs.length > 0
      ? 'both'
      : otps.length > 0
      ? 'otp'
      : subs.length > 0
      ? 'subscription'
      : 'none';

  const categories = products.filter((p) => p.collectionType === 'category');
  const programs = products.filter((p) => p.collectionType === 'program');
  const publishedCollectionType =
    categories.length > 0 && programs.length > 0
      ? 'both'
      : categories.length > 0
      ? 'category'
      : programs.length > 0
      ? 'program'
      : 'none';

  return { products: products, publishedProductType, publishedCollectionType };
};

export const usePublishedProducts = (p: {
  influencerId?: string;
}): Async<PublishedProducts> => {
  const user = useUser();
  const query = useGetAllProgramsQuery({
    variables: {
      id: parseInt(`${p.influencerId || user?.influencer?.id || 0}`, 10),
    },
    fetchPolicy: 'network-only',
  });
  const res = useAsyncQuery(query, 'creator', toPublishedPrograms);
  return res.data;
};
